import { projectsAPI } from '../services/api';

/**
 * Puppeteer + PixiJS Export Utility (Frontend)
 * Separate from canvasExportUtils.js for parallel testing
 */

// export const EXPORT_RESOLUTIONS = {
//   '720p': { width: 1280, height: 720, label: '720p (1280×720)' },
//   '1080p': { width: 1920, height: 1080, label: '1080p (1920×1080)' },
//   '4K': { width: 3840, height: 2160, label: '4K (3840×2160)' }
// };

// Base "reference" resolutions (the official 16:9 values)
// const BASE_RESOLUTIONS = {
//   '720p': { width: 1280, height: 720 },
//   '1080p': { width: 1920, height: 1080 },
//   '4K': { width: 3840, height: 2160 }
// };

// Compute actual output size that respects canvas aspect ratio
export const getExportResolution = (canvasSize) => {
  const width = canvasSize.width;
  const height = canvasSize.height;

  // Ensure even dimensions (required by h264)
  return {
    width: width + (width % 2),
    height: height + (height % 2),
  };
};

/**
 * Prepare canvas elements for export by extracting all necessary data
 * Properly handle x/y and scaleX/scaleY properties
 */
export const prepareElementsForPuppeteerExport = (canvasElements) => {
  const TYPEWRITER_ANIMATION = 'typewriter';
  const normalizeCaptionStyleForExport = (style = {}, captionSprite = null) => {
    const normalizedStyle = { ...(style || {}) };

    const displayMode = normalizedStyle.displayMode || 'karaoke';
    const isTypewriterMode = (
      normalizedStyle.animation === TYPEWRITER_ANIMATION ||
      normalizedStyle.displayMode === TYPEWRITER_ANIMATION
    );

    const revealOpacitySupported = (
      displayMode === 'phraseHighlight' ||
      displayMode === 'progressive' ||
      displayMode === 'phraseStack'
    );

    if (displayMode !== 'phraseHighlight') {
      normalizedStyle.highlightFromPhraseIndex = 0;
    }

    if (!revealOpacitySupported) {
      normalizedStyle.revealOpacityMode = null;
      normalizedStyle.inactiveOpacity = null;
    }

    if (!isTypewriterMode) {
      normalizedStyle.typewriterCursor = null;
      normalizedStyle.typewriterBackgroundColor = null;
      normalizedStyle.typewriterBackgroundPaddingX = null;
      normalizedStyle.typewriterBackgroundPaddingY = null;
      normalizedStyle.typewriterBackgroundRadius = null;
    }

    if (!normalizedStyle.activeWordBlur) {
      normalizedStyle.activeWordBlurColor = null;
      normalizedStyle.activeWordBlurRadius = null;
      normalizedStyle.activeWordBlurTrailMs = null;
      normalizedStyle.activeWordBlurRevealMs = null;
      normalizedStyle.activeWordBlurOpacity = null;
      normalizedStyle.activeWordBlurOffsetX = null;
      normalizedStyle.activeWordBlurOffsetY = null;
      normalizedStyle.activeWordBlurWordAlpha = null;
      normalizedStyle.activeWordBlurXRatio = null;
    }

    if (captionSprite && Number.isFinite(Number(captionSprite.x))) {
      normalizedStyle.x = Number(captionSprite.x);
    }
    if (captionSprite && Number.isFinite(Number(captionSprite.y))) {
      normalizedStyle.y = Number(captionSprite.y);
    }

    return normalizedStyle;
  };

  return canvasElements.map(element => {
    // Remove PIXI-specific properties and circular references
    const {
      pixiSprite,
      captionSprite,
      sprite,
      pixiText,
      pixiSound,
      _wasVisible,
      ...exportableElement
    } = element;

    // Normalize position and scale properties
    const exportData = {
      id: exportableElement.id,
      type: exportableElement.type,
      name: exportableElement.name,

      // Timeline data (critical for sync)
      timeline: {
        inPoint: exportableElement.timeline?.inPoint || 0,
        outPoint: exportableElement.timeline?.outPoint || 0,
        croppedInPoint: exportableElement.timeline?.croppedInPoint || 0,
        croppedOutPoint: exportableElement.timeline?.croppedOutPoint || null,
      },

      //Transform data - handle both formats
      position: {
        x: exportableElement.position?.x ?? exportableElement.x ?? 0,
        y: exportableElement.position?.y ?? exportableElement.y ?? 0
      },
      scale: {
        x: exportableElement.scale?.x ?? exportableElement.scaleX ?? 1,
        y: exportableElement.scale?.y ?? exportableElement.scaleY ?? 1
      },
      rotation: exportableElement.rotation || 0,
      opacity: exportableElement.opacity !== undefined ? exportableElement.opacity :
        (exportableElement.alpha !== undefined ? exportableElement.alpha : 1),
      anchor: exportableElement.anchor || { x: 0.5, y: 0.5 },

      //Include actual dimensions
      width: exportableElement.width,
      height: exportableElement.height,

      // Media-specific data
      componentData: exportableElement.componentData,

      // For video elements
      playbackMode: exportableElement.playbackMode || 'pause',

      // For audio/video elements
      volume: exportableElement.volume !== undefined ? exportableElement.volume : 1,
      muted: exportableElement.muted || false,

      // Z-index for layering
      zIndex: exportableElement.zIndex || 0,

      // Additional properties
      ...exportableElement
    };

    // For media elements, include file reference
    if (
      element.type === 'video' ||
      element.type === 'audio' ||
      element.type === 'image' ||
      element.type === 'gif' ||
      element.type === 'component'
    ) {
      const url =
        exportableElement.componentData?.currSrc ||   // ← Most important: rendered component video
        exportableElement.componentData?.url ||
        exportableElement.src;

      if (url) {
        exportData.mediaUrl = url;
        exportData.mediaId = exportableElement.componentData?.id || exportableElement.id;
      }
    }

    // AI sprites: resolve to their underlying media type for export
    if (element.type === 'ai' && exportableElement.componentData?.url && exportableElement.componentData?.aiMediaType) {
      exportData.mediaUrl = exportableElement.componentData.url;
      exportData.mediaId = exportableElement.componentData.id || exportableElement.id;
      exportData.aiMediaType = exportableElement.componentData.aiMediaType;
    }

    // Captions: prefer the live runtime style from the canvas word-container when available.
    if (exportableElement.componentData?.captions) {
      const currentCaptions = exportableElement.componentData.captions;
      const runtimeCaptionStyle = (
        captionSprite &&
        captionSprite._captionStyle &&
        typeof captionSprite._captionStyle === 'object'
      )
        ? captionSprite._captionStyle
        : null;

      const mergedCaptionStyle = normalizeCaptionStyleForExport(
        {
          ...(currentCaptions.style || {}),
          ...(runtimeCaptionStyle || {}),
          ...(
            !runtimeCaptionStyle &&
            captionSprite &&
            captionSprite._isWordContainer &&
            captionSprite._displayMode
              ? { displayMode: captionSprite._displayMode }
              : {}
          ),
        },
        captionSprite
      );

      exportData.componentData = {
        ...exportData.componentData,
        captions: {
          ...currentCaptions,
          style: mergedCaptionStyle,
        },
      };
    }

    return exportData;
  });
};

/**
 * Export canvas video using Puppeteer + PixiJS
 * @param {Object} options - Configuration options
 * @param {Function} onProgress - Progress callback (stage, current, total, percentage, message)
 * @returns {Promise<string>} Download URL for the exported video
 */
export const exportCanvasVideoWithPuppeteer = async (options, onProgress) => {
  const {
    canvasSize,
    canvasElements,
    duration,
    projectId,
    projectName,
    backgroundColor,
    resolution = '1080p',
    fps = 25,
  } = options;

  const { width: exportWidth, height: exportHeight } = getExportResolution(canvasSize, resolution);

  let jobId = null;

  try {

    if (onProgress) {
      onProgress('preparing', 0, 100, 5, 'Preparing composition data...');
    }

    const preparedElements = prepareElementsForPuppeteerExport(canvasElements);

    if (onProgress) {
      onProgress('initializing', 0, 100, 10, 'Initializing Export...');
    }

    const exportPayload = {
      projectId,
      projectName,
      resolution,
      fps,
      duration,
      canvasWidth: canvasSize.width,
      canvasHeight: canvasSize.height,
      exportWidth,
      exportHeight,
      elements: preparedElements,
      backgroundColor: backgroundColor || '#000000',
      format: options.format || 'mp4',
    };

    const initResponse = await projectsAPI.initializePuppeteerExport(exportPayload);
    jobId = initResponse.data.jobId;

    // console.log('[Puppeteer Export] Job started:', jobId);

    // Notify about jobId immediately - this triggers the notification system to start polling
    if (onProgress) {
      onProgress('initializing', 0, 100, 10, 'Export job created...', jobId);
    }

    let exportComplete = false;
    let downloadUrl = null;
    let pollAttempts = 0;
    const MAX_POLL_ATTEMPTS = 600;

    let currentPollInterval = 2000;
    const MAX_POLL_INTERVAL = 10000;

    while (!exportComplete && pollAttempts < MAX_POLL_ATTEMPTS) {
      await new Promise(resolve => setTimeout(resolve, currentPollInterval));

      // Increase interval for next loop (multiply by 1.2), capped at MAX_POLL_INTERVAL
      currentPollInterval = Math.min(currentPollInterval * 1.2, MAX_POLL_INTERVAL);

      pollAttempts++;

      try {
        const statusResponse = await projectsAPI.getPuppeteerExportStatus({ jobId });
        const jobData = statusResponse.data.data || statusResponse.data;
        const { status, progress, stage, message, downloadUrl: url, error } = jobData;

        console.log(`[Puppeteer Export Poll ${pollAttempts}] ${stage}: ${progress}% (status: ${status})`);

        // Check for failed/cancelled status FIRST
        if (status === 'failed' || status === 'cancelled' || stage === 'error') {
          console.error('[Puppeteer Export] ❌ Job failed or cancelled:', error);
          throw new Error(error || 'Export failed on server');
        }

        // Map stages
        let frontendStage = 'processing';
        if (stage === 'initializing') frontendStage = 'initializing';
        else if (stage === 'downloading_media') frontendStage = 'downloading';
        else if (stage === 'rendering') frontendStage = 'rendering';
        else if (stage === 'encoding') frontendStage = 'encoding';
        else if (status === 'completed' || stage === 'complete') frontendStage = 'complete';

        if (onProgress) {
          const adjustedProgress = Math.min(progress || 0, 100);
          onProgress(
            frontendStage,
            adjustedProgress,
            100,
            Math.max(10, adjustedProgress),
            message || `${stage}... ${adjustedProgress}%`
          );
        }

        // Check if completed
        if (status === 'completed' || stage === 'complete') {
          exportComplete = true;
          downloadUrl = url;
          console.log('[Puppeteer Export] ✅ Complete! URL:', downloadUrl);

          if (onProgress) {
            onProgress('complete', 100, 100, 100, 'Export complete!');
          }
          break;
        }

      } catch (pollError) {
        // Handle 429 specifically
        if (pollError.response && pollError.response.status === 429) {
          console.warn(`[Export Poll] Hit rate limit (429). Backing off for 15 seconds...`);
          await new Promise(resolve => setTimeout(resolve, 15000));
          continue;
        }

        console.error(`[Puppeteer Export Poll ${pollAttempts}] Error:`, pollError);
        throw pollError;
      }
    }

    if (!exportComplete) {
      throw new Error('Export timeout - job did not complete in time');
    }

    if (!downloadUrl) {
      throw new Error('No download URL provided');
    }

    return downloadUrl;

  } catch (error) {
    console.error('[Puppeteer Export] Failed:', error);

    if (onProgress) {
      onProgress('error', 0, 100, 0, error.message || 'Export failed');
    }

    throw error;
  }
};

/**
 * Download video
 */
export const downloadVideo = (downloadUrl, filename = 'export.mp4') => {
  const a = document.createElement('a');
  a.href = downloadUrl;
  a.download = filename;
  a.target = '_blank';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

/**
 * Get export filename
 */
export const getExportFilename = (projectName, format = 'mp4') => {
  const timestamp = new Date().toISOString().slice(0, 10);
  const sanitizedName = projectName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  const ext = format === 'gif' ? 'gif' : 'mp4';
  return `${sanitizedName}_${timestamp}.${ext}`;
};
